import React, { useState, useEffect, useRef } from 'react';
import { TextInput, Select, Badge, Spinner } from 'flowbite-react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { toast } from 'react-toastify';
import Title from '../../components/Title';
import Button from '../../components/Button';
import PaginationFlowBite from '../../components/PaginationFlowBite';
import { searchBooks, downloadPDF, replacePDF, FlippingBook } from '../../api/flipping';
import ReplacePDFModal from './ReplacePDFModal';

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString();
};

const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case 'READY':
      return 'success';
    case 'CONVERTING':
    case 'UPLOADED':
      return 'warning';
    case 'FAILED':
      return 'failure';
    default:
      return 'gray';
  }
};

const BookManagement: React.FC = () => {
  try {
  const { user } = useFusionAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [books, setBooks] = useState<FlippingBook[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // For replace flow (modal handled in C4)
  const [selectedBook, setSelectedBook] = useState<FlippingBook | null>(null);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchBooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await searchBooks({
        q: debouncedQuery || undefined,
        status: statusFilter || undefined,
        limit: perPage,
        offset: (page - 1) * perPage,
      });
      setBooks(response.books || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error('Error fetching books:', err);
      const serverMessage = err?.response?.data?.error?.message;
      setError(serverMessage || (err instanceof Error ? err.message : 'Failed to fetch books'));
      setBooks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, [debouncedQuery, statusFilter, page, perPage]);

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPage = (value: any) => {
    setPerPage(value.value);
    setPage(1);
  };

  const handleDownload = async (book: FlippingBook) => {
    setDownloadingId(book.bookId);
    try {
      const blob = await downloadPDF(book.bookId);
      const title = book.info.title || 'Untitled';
      const filename = `${title}-${book.bookId.substring(0, 8)}.pdf`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      const msg = err?.response?.data?.error?.message || 'Failed to download PDF. Please try again.';
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleReplaceClick = (book: FlippingBook) => {
    setSelectedBook(book);
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    e.target.value = '';

    // Validate file extension
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file.');
      return;
    }

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 100MB.');
      return;
    }

    setReplacementFile(file);
    setShowReplaceModal(true);
  };

  const handleReplace = async (reason: string) => {
    if (!selectedBook || !replacementFile) return;
    setIsReplacing(true);
    try {
      const operatorEmail = user?.email || '';
      await replacePDF(selectedBook.bookId, replacementFile, operatorEmail, reason || undefined);
      toast.success('PDF replaced successfully. The book will be reprocessed.');
      setShowReplaceModal(false);
      setSelectedBook(null);
      setReplacementFile(null);
      fetchBooks();
    } catch (err: any) {
      console.error('Error replacing PDF:', err);
      const msg = err?.response?.data?.error?.message || 'Failed to replace PDF. Please try again.';
      toast.error(msg);
    } finally {
      setIsReplacing(false);
    }
  };

  const totalPages = Math.ceil(total / perPage) || 1;

  const tablePagination = {
    pageSize: perPage,
    total: total,
    lastPage: totalPages,
    currentPage: page,
    onChange: handlePageChange,
  };

  // Error boundary
  if (error) {
    return (
      <div className="pt-3 min-h-screen pb-[100px]">
        <Title>PDF Book Management</Title>
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">Error loading books</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <Button
            variant="LIGHT"
            onClick={() => {
              setError(null);
              fetchBooks();
            }}
            className="mt-3"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3 min-h-screen pb-[100px]">
      <Title>PDF Book Management</Title>

      <div className="shadow-sm bg-white rounded-lg mt-6">
        <div className="flex justify-between border-b border-neutral-200 py-[22px] px-6 gap-6">
          <div className="flex gap-4 flex-1">
            <TextInput
              placeholder="Search by customer email, name, or book ID..."
              className="w-[400px]"
              value={searchQuery}
              onChange={handleTextInput}
            />
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              className="w-[200px]"
            >
              <option value="">All Status</option>
              <option value="READY">READY</option>
              <option value="UPLOADED">UPLOADED</option>
              <option value="CONVERTING">CONVERTING</option>
              <option value="FAILED">FAILED</option>
            </Select>
          </div>
          <div className="text-gray-600">
            Total books: {total}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3">Title</th>
                    <th scope="col" className="px-6 py-3">Customer Name</th>
                    <th scope="col" className="px-6 py-3">Customer Email</th>
                    <th scope="col" className="px-6 py-3">Status</th>
                    <th scope="col" className="px-6 py-3">Pages</th>
                    <th scope="col" className="px-6 py-3">File Size</th>
                    <th scope="col" className="px-6 py-3">Updated</th>
                    <th scope="col" className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((book) => (
                    <tr
                      key={book.bookId}
                      className="bg-white border-b hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {book.info.title || 'Untitled'}
                      </td>
                      <td className="px-6 py-4">
                        {book.customer?.name || '\u2014'}
                      </td>
                      <td className="px-6 py-4">
                        {book.customer?.email || '\u2014'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge color={getStatusBadgeColor(book.status)}>
                          {book.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {book.pageCount ?? '\u2014'}
                      </td>
                      <td className="px-6 py-4">
                        {formatFileSize(book.fileSizeBytes)}
                      </td>
                      <td className="px-6 py-4">
                        {formatDate(book.updatedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            variant="OUTLINED"
                            size="xs"
                            onClick={() => handleDownload(book)}
                            disabled={downloadingId === book.bookId}
                            isProcessing={downloadingId === book.bookId}
                          >
                            Download
                          </Button>
                          <Button
                            variant="DANGER"
                            size="xs"
                            onClick={() => handleReplaceClick(book)}
                            disabled={book.status !== 'READY'}
                          >
                            Replace
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {books.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-4 text-center">
                        No books found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <PaginationFlowBite
                tablePagination={tablePagination}
                handlePerPage={handlePerPage}
              />
            )}
          </>
        )}
      </div>

      {/* Hidden file input for PDF replacement */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".pdf"
        onChange={handleFileSelected}
        className="hidden"
      />

      <ReplacePDFModal
        show={showReplaceModal}
        onClose={() => {
          setShowReplaceModal(false);
          setSelectedBook(null);
          setReplacementFile(null);
        }}
        book={selectedBook}
        file={replacementFile}
        onConfirm={handleReplace}
        isProcessing={isReplacing}
      />
    </div>
  );
  } catch (renderError) {
    console.error('BookManagement render error:', renderError);
    return (
      <div className="pt-3 min-h-screen pb-[100px]">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-medium">Component Error</h3>
          <p className="text-red-600 mt-1">
            There was an error rendering the book management page.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Check the browser console for details.
          </p>
        </div>
      </div>
    );
  }
};

export default BookManagement;
